import { brushAreaPolygon, clipPolygonToBounds, pointInPolygon, polygonsIntersect } from "./geometry";
import type { MaterialRegion, Point, WaterRegion, WaterType, WeatherDryRegion } from "../types";

export const DEFAULT_WATER_TYPE: WaterType = "ocean";
export const DEFAULT_WATER_MOVEMENT = 0.65;
export const DEFAULT_WATER_OPACITY = 0.58;

export function clampWaterSetting(value: number | undefined, fallback: number) {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value!)) : fallback;
}

type RegionBounds = { width: number; height: number };

export type PaintedBoundaryHandle = { polygonIndex: number; pointIndex: number; point: Point };

/**
 * Chooses real polygon vertices for the border editor without flooding the DM
 * view with hundreds of controls on a long freehand stroke.
 */
export function paintedBoundaryHandles(polygons: Point[][], maximumHandles = 72): PaintedBoundaryHandle[] {
  const usable = polygons.map((polygon, polygonIndex) => ({ polygon, polygonIndex })).filter(({ polygon }) => polygon.length >= 3);
  if (!usable.length || maximumHandles <= 0) return [];
  const totalPoints = usable.reduce((total, { polygon }) => total + polygon.length, 0);
  if (totalPoints <= maximumHandles) return usable.flatMap(({ polygon, polygonIndex }) => polygon.map((point, pointIndex) => ({ polygonIndex, pointIndex, point })));
  const allocations: number[] = usable.map((_, index) => index < maximumHandles ? 1 : 0); let remaining = Math.max(0, maximumHandles - allocations.reduce((sum, count) => sum + count, 0));
  while (remaining) {
    let distributed = false;
    for (let index = 0; index < usable.length && remaining; index += 1) {
      if (allocations[index] >= usable[index].polygon.length) continue;
      allocations[index] += 1; remaining -= 1; distributed = true;
    }
    if (!distributed) break;
  }
  return usable.flatMap(({ polygon, polygonIndex }, polygonListIndex) => {
    const count = Math.min(polygon.length, allocations[polygonListIndex]);
    if (!count) return [];
    const indexes = Array.from({ length: count }, (_, index) => Math.floor(index * polygon.length / count));
    return indexes.map((pointIndex) => ({ polygonIndex, pointIndex, point: polygon[pointIndex] }));
  });
}

/** Moves one exposed border control while keeping the entire shape on-map. */
export function movePaintedBoundaryPoint(polygons: Point[][], polygonIndex: number, pointIndex: number, point: Point, bounds: RegionBounds): Point[][] {
  if (!Number.isInteger(polygonIndex) || !Number.isInteger(pointIndex) || polygonIndex < 0 || pointIndex < 0) return polygons;
  const polygon = polygons[polygonIndex]; if (!polygon || pointIndex >= polygon.length) return polygons;
  const boundedPoint = { x: Math.max(0, Math.min(bounds.width, point.x)), y: Math.max(0, Math.min(bounds.height, point.y)) };
  return polygons.map((candidate, candidateIndex) => candidateIndex === polygonIndex
    ? candidate.map((candidatePoint, candidatePointIndex) => candidatePointIndex === pointIndex ? boundedPoint : candidatePoint)
    : candidate);
}

const boundedPolygons = (polygons: Point[][], bounds?: RegionBounds) => bounds
  ? polygons.map((polygon) => clipPolygonToBounds(polygon, bounds)).filter((polygon) => polygon.length >= 3)
  : polygons;

export function paintedRegionPolygons(region: { points: Point[]; polygons?: Point[][] }, bounds?: RegionBounds) {
  const polygons = region.polygons?.filter((polygon) => polygon.length >= 3) ?? [];
  return boundedPolygons(polygons.length ? polygons : region.points.length >= 3 ? [region.points] : [], bounds);
}

function convexHull(points: Point[]): Point[] {
  const unique = [...new Map(points.map((point) => [`${point.x},${point.y}`, point])).values()].sort((a, b) => a.x - b.x || a.y - b.y);
  if (unique.length <= 2) return unique;
  const cross = (origin: Point, a: Point, b: Point) => (a.x - origin.x) * (b.y - origin.y) - (a.y - origin.y) * (b.x - origin.x);
  const lower: Point[] = []; for (const point of unique) { while (lower.length >= 2 && cross(lower.at(-2)!, lower.at(-1)!, point) <= 0) lower.pop(); lower.push(point); }
  const upper: Point[] = []; for (const point of [...unique].reverse()) { while (upper.length >= 2 && cross(upper.at(-2)!, upper.at(-1)!, point) <= 0) upper.pop(); upper.push(point); }
  lower.pop(); upper.pop(); return [...lower, ...upper];
}

/** Old dry strokes stored a self-crossing ribbon; treat their outer hull as one shelter. */
export function weatherDryRegionPolygons(region: WeatherDryRegion, bounds?: RegionBounds) {
  const polygons = paintedRegionPolygons(region);
  return boundedPolygons(region.areaModel === "brush-union" ? polygons : polygons.map(convexHull).filter((polygon) => polygon.length >= 3), bounds);
}

/** Rebuild legacy water ribbons from their saved brush path; new water already stores the union. */
export function waterRegionPolygons(region: WaterRegion, bounds?: RegionBounds) {
  const polygons = paintedRegionPolygons(region);
  const rebuilt = region.areaModel === "brush-union" ? polygons : polygons.map((polygon, index) => {
    const path = region.flowPaths?.[index]; const radius = Math.max(0.05, region.brushRadii?.[index] ?? 0.75);
    return path?.length ? brushAreaPolygon(path, radius) : polygon;
  });
  return boundedPolygons(rebuilt, bounds);
}

export function waterRegionErasePolygons(region: WaterRegion, bounds?: RegionBounds) {
  return boundedPolygons((region.erasePolygons ?? []).filter((polygon) => polygon.length >= 3), bounds);
}

export function pointInWaterRegion(region: WaterRegion, point: Point, bounds?: RegionBounds) {
  return waterRegionPolygons(region, bounds).some((polygon) => pointInPolygon(point, polygon))
    && !waterRegionErasePolygons(region, bounds).some((polygon) => pointInPolygon(point, polygon));
}

export function addWaterEraseToRegion(region: WaterRegion, erasePolygon: Point[], bounds: RegionBounds): WaterRegion {
  const clipped = clipPolygonToBounds(erasePolygon, bounds);
  if (clipped.length < 3 || !waterRegionPolygons(region, bounds).some((polygon) => polygonsIntersect(polygon, clipped))) return region;
  return { ...region, erasePolygons: [...waterRegionErasePolygons(region, bounds), clipped] };
}

function inferredFlowPath(polygon: Point[]) {
  if (polygon.length < 2) return polygon;
  let bestA = polygon[0]; let bestB = polygon[1]; let bestDistance = 0;
  for (let a = 0; a < polygon.length; a += Math.max(1, Math.floor(polygon.length / 24))) {
    for (let b = a + 1; b < polygon.length; b += Math.max(1, Math.floor(polygon.length / 24))) {
      const candidate = (polygon[a].x - polygon[b].x) ** 2 + (polygon[a].y - polygon[b].y) ** 2;
      if (candidate > bestDistance) { bestDistance = candidate; bestA = polygon[a]; bestB = polygon[b]; }
    }
  }
  return [bestA, bestB];
}

export function waterRegionFlowPaths(region: WaterRegion) {
  return paintedRegionPolygons(region).map((polygon, index) => {
    const stored = region.flowPaths?.[index];
    return stored && stored.length >= 2 ? stored : inferredFlowPath(polygon);
  });
}

export function waterRegionBrushRadii(region: WaterRegion) {
  return paintedRegionPolygons(region).map((_, index) => Math.max(0.05, region.brushRadii?.[index] ?? 0.75));
}

export function mergeWaterRegions(regions: WaterRegion[], selectedId: string, bounds?: RegionBounds): WaterRegion | undefined {
  const selected = regions.find((region) => region.id === selectedId);
  if (!selected) return undefined;
  const polygons = regions.flatMap((region) => waterRegionPolygons(region, bounds));
  if (!polygons.length) return undefined;
  return {
    id: selected.id,
    points: polygons[0],
    polygons,
    areaModel: "brush-union",
    flowPaths: regions.flatMap(waterRegionFlowPaths),
    erasePolygons: regions.flatMap((region) => waterRegionErasePolygons(region, bounds)),
    brushRadii: regions.flatMap(waterRegionBrushRadii),
    waterType: selected.waterType ?? DEFAULT_WATER_TYPE,
    flowDirection: Number.isFinite(selected.flowDirection) ? selected.flowDirection : 0,
    movement: clampWaterSetting(selected.movement, DEFAULT_WATER_MOVEMENT),
    opacity: clampWaterSetting(selected.opacity, DEFAULT_WATER_OPACITY),
    createdAt: Math.min(...regions.map((region) => region.createdAt)),
  };
}

export function mergeWeatherDryRegions(regions: WeatherDryRegion[], selectedId: string, bounds?: RegionBounds): WeatherDryRegion | undefined {
  const selected = regions.find((region) => region.id === selectedId);
  if (!selected) return undefined;
  const polygons = regions.flatMap((region) => weatherDryRegionPolygons(region, bounds));
  if (!polygons.length) return undefined;
  return { id: selected.id, points: polygons[0], polygons, areaModel: "brush-union", createdAt: Math.min(...regions.map((region) => region.createdAt)) };
}

export function mergeMaterialRegions(regions: MaterialRegion[], selectedId: string, bounds?: RegionBounds): MaterialRegion | undefined {
  const selected = regions.find((region) => region.id === selectedId);
  if (!selected) return undefined;
  const compatible = regions.filter((region) => region.material === selected.material);
  const polygons = compatible.flatMap((region) => paintedRegionPolygons(region, bounds));
  if (!polygons.length) return undefined;
  return { ...selected, points: polygons[0], polygons, areaModel: "brush-union", createdAt: Math.min(...compatible.map((region) => region.createdAt)) };
}

const overlappingRegionIndexes = <T>(regions: T[], additionPolygons: Point[][], polygons: (region: T) => Point[][]) => regions
  .map((region, index) => polygons(region).some((existing) => additionPolygons.some((addition) => polygonsIntersect(existing, addition))) ? index : -1)
  .filter((index) => index >= 0);

export function addWaterBrushToRegions(regions: WaterRegion[], addition: WaterRegion, bounds: RegionBounds): { regions: WaterRegion[]; regionId: string } {
  const additionPolygons = waterRegionPolygons(addition, bounds);
  const overlapping = overlappingRegionIndexes(regions, additionPolygons, (region) => waterRegionPolygons(region, bounds));
  if (!overlapping.length) return { regions: [...regions, addition], regionId: addition.id };
  const targetIndex = overlapping[0]; const target = regions[targetIndex];
  const merged = mergeWaterRegions([...overlapping.map((index) => regions[index]), addition], target.id, bounds);
  if (!merged) return { regions, regionId: target.id };
  const removed = new Set(overlapping); const next = regions.filter((_, index) => !removed.has(index));
  next.splice(regions.slice(0, targetIndex).filter((_, index) => !removed.has(index)).length, 0, merged);
  return { regions: next, regionId: target.id };
}

export function addWeatherDryBrushToRegions(regions: WeatherDryRegion[], addition: WeatherDryRegion, bounds: RegionBounds): { regions: WeatherDryRegion[]; regionId: string } {
  const additionPolygons = weatherDryRegionPolygons(addition, bounds);
  const overlapping = overlappingRegionIndexes(regions, additionPolygons, (region) => weatherDryRegionPolygons(region, bounds));
  if (!overlapping.length) return { regions: [...regions, addition], regionId: addition.id };
  const targetIndex = overlapping[0]; const target = regions[targetIndex];
  const merged = mergeWeatherDryRegions([...overlapping.map((index) => regions[index]), addition], target.id, bounds);
  if (!merged) return { regions, regionId: target.id };
  const removed = new Set(overlapping); const next = regions.filter((_, index) => !removed.has(index));
  next.splice(regions.slice(0, targetIndex).filter((_, index) => !removed.has(index)).length, 0, merged);
  return { regions: next, regionId: target.id };
}

export function addMaterialBrushToRegions(regions: MaterialRegion[], addition: MaterialRegion, bounds: RegionBounds): { regions: MaterialRegion[]; regionId: string } {
  const additionPolygons = paintedRegionPolygons(addition, bounds);
  const overlapping = overlappingRegionIndexes(regions, additionPolygons, (region) => region.material === addition.material ? paintedRegionPolygons(region, bounds) : []);
  if (!overlapping.length) return { regions: [...regions, addition], regionId: addition.id };
  const targetIndex = overlapping[0]; const target = regions[targetIndex];
  const merged = mergeMaterialRegions([...overlapping.map((index) => regions[index]), addition], target.id, bounds);
  if (!merged) return { regions, regionId: target.id };
  const removed = new Set(overlapping); const next = regions.filter((_, index) => !removed.has(index));
  next.splice(regions.slice(0, targetIndex).filter((_, index) => !removed.has(index)).length, 0, merged);
  return { regions: next, regionId: target.id };
}

export function eraseWaterRegionAtPoint(region: WaterRegion, point: Point, bounds: RegionBounds): { erased: boolean; region?: WaterRegion } {
  const polygons = waterRegionPolygons(region, bounds); const erasedIndex = polygons.findIndex((polygon) => pointInPolygon(point, polygon));
  if (erasedIndex < 0) return { erased: false, region };
  const remaining = polygons.filter((_, index) => index !== erasedIndex);
  if (!remaining.length) return { erased: true };
  return {
    erased: true,
    region: {
      ...region,
      points: remaining[0],
      polygons: remaining,
      areaModel: "brush-union",
      flowPaths: waterRegionFlowPaths(region).filter((_, index) => index !== erasedIndex),
      brushRadii: waterRegionBrushRadii(region).filter((_, index) => index !== erasedIndex),
    },
  };
}

export function eraseWeatherDryRegionAtPoint(region: WeatherDryRegion, point: Point, bounds: RegionBounds): { erased: boolean; region?: WeatherDryRegion } {
  const polygons = weatherDryRegionPolygons(region, bounds); const erasedIndex = polygons.findIndex((polygon) => pointInPolygon(point, polygon));
  if (erasedIndex < 0) return { erased: false, region };
  const remaining = polygons.filter((_, index) => index !== erasedIndex);
  return remaining.length
    ? { erased: true, region: { ...region, points: remaining[0], polygons: remaining, areaModel: "brush-union" } }
    : { erased: true };
}

export function eraseMaterialRegionAtPoint(region: MaterialRegion, point: Point, bounds: RegionBounds): { erased: boolean; region?: MaterialRegion } {
  const polygons = paintedRegionPolygons(region, bounds); const erasedIndex = polygons.findIndex((polygon) => pointInPolygon(point, polygon));
  if (erasedIndex < 0) return { erased: false, region };
  const remaining = polygons.filter((_, index) => index !== erasedIndex);
  return remaining.length
    ? { erased: true, region: { ...region, points: remaining[0], polygons: remaining, areaModel: "brush-union" } }
    : { erased: true };
}
